// NotificationBanner.tsx
import React from "react";
import { Box, Text, Paper, Group } from "@mantine/core";
import InfoIcon from "../../../assets/InfoIcon.svg";

interface NotificationBannerProps {
  /**
   * The message to display in the notification
   */
  message: string;

  /**
   * Background color for the notification
   * @default "#FFF4E5" (light orange)
   */
  bgColor?: string;

  /**
   * Text color for the notification
   * @default "#663C00" (dark brown)
   */
  textColor?: string;

  /**
   * Icon color
   * @default "#F59E0B" (orange)
   */
  iconColor?: string;

  /**
   * Optional CSS class name
   */
  className?: string;

  /**
   * Show the fill info text in the top right
   * @default false
   */
  showFillInfo?: boolean;

  /**
   * Fill text to show in the top right
   * @default "Fill (263.33) + Fill (160)"
   */
  fillInfoText?: string;
}

/**
 * A reusable notification banner component for displaying warnings or informational messages
 */
const NotificationBanner: React.FC<NotificationBannerProps> = ({
  message,
  bgColor = "#FFF4E5",
  textColor = "#663C00",
  iconColor = "#F59E0B",
  className,
  showFillInfo = false,
  fillInfoText = "Fill (263.33) + Fill (160)",
}) => {
  return (
    <Paper
      p="6px 8px"
      className={className}
      style={{
        backgroundColor: bgColor,
        border: "1px solid",
        borderColor: iconColor,
        borderRadius: "4px",
        position: "relative",
        width: "100%",
        boxSizing: "border-box",
        flexShrink: 0,
      }}
    >
      {showFillInfo && (
        <Text
          size="12px"
          style={{
            position: "absolute",
            top: "2px",
            right: "8px",
            color: "#9333EA",
            fontSize: "10px",
            opacity: 0.8,
          }}
        >
          {fillInfoText}
        </Text>
      )}

      <Group align="flex-start" wrap="nowrap" gap="xs">
        <Box
          component="img"
          height={20}
          width={20}
          src={InfoIcon}
          style={{ flexShrink: 0, marginTop: 2 }}
        />
        <Text
          size="xs"
          c={textColor}
          style={{
            flex: 1,
            minWidth: 0,
            wordBreak: "break-word",
            fontFamily: "Roboto, sans-serif",
            fontWeight: 400,
            lineHeight: 1.5,
          }}
        >
          {message}
        </Text>
      </Group>
    </Paper>
  );
};

export default NotificationBanner;
